import { useCallback, useEffect, useState } from 'react';
import { stationHealth, type Station, type SyncStatus } from '@visionqc/shared';
import type { Customer } from '@visionqc/shared';
import { api } from '../api/client';
import { Combobox } from '../components/Combobox';
import { CustomerPill } from '../components/CustomerPill';
import { StationDetailView } from './StationDetail';
import { useIsAdmin } from '../api/auth';
import { fmtTime } from '../lib/format';

/**
 * Fleet dashboard.
 *
 * Two questions, and nothing else: is every station still talking to us, and is
 * anything stuck on its way up. A station that stops syncing keeps inspecting
 * perfectly well — that is by design — so silence here is never an emergency on
 * the line, only a gap in what central knows.
 */

const HEALTH: Record<string, { label: string; color: string; icon: string }> = {
  // Status colours are the reserved good/warning/serious set, and each one ships
  // with an icon and a word: never colour alone.
  online: { label: 'Online', color: 'var(--good)', icon: '●' },
  late: { label: 'Late', color: 'var(--warn)', icon: '▲' },
  offline: { label: 'Offline', color: 'var(--bad)', icon: '■' },
};

export function FleetView() {
  const isAdmin = useIsAdmin();
  const [stations, setStations] = useState<Station[]>([]);
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<{ name: string; key: string } | null>(null);
  const [openStation, setOpenStation] = useState<number | null>(null);
  const [name, setName] = useState('');
  /* A station must belong to a customer — it is the billable unit. */
  const [enrolCustomerId, setEnrolCustomerId] = useState<number | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  useEffect(() => {
    void api.listCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, st] = await Promise.all([
      api.listStations().catch(() => []),
      api.syncStatus().catch(() => null),
    ]);
    setStations(s);
    setStatus(st);
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <h1 className="view-title">Fleet</h1>
        <div className="panel muted">Managing stations requires the ADMIN role.</div>
      </div>
    );
  }

  // A detail page rather than a route: the fleet list is the way in, and
  // deep-linking to a station nobody has enrolled yet would only 404.
  if (openStation !== null) {
    return (
      <StationDetailView
        id={openStation}
        onBack={() => {
          setOpenStation(null);
          void load();
        }}
      />
    );
  }

  const enrol = async () => {
    setBusy(true);
    try {
      if (enrolCustomerId === null) {
        setError('Pick the customer this station belongs to — it is what gets billed.');
        return;
      }
      const created = await api.createStation({ name: name.trim(), customerId: enrolCustomerId });
      setNewKey({ name: name.trim(), key: created.key });
      setName('');
      setEnrolCustomerId(null);
      setError(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const totals = stations.reduce(
    (a, s) => ({ inspections: a.inspections + s.inspections, annotations: a.annotations + s.annotations }),
    { inspections: 0, annotations: 0 },
  );

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Fleet</h1>
        <button className="btn sm" onClick={() => void load()}>
          Refresh
        </button>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}

      {/* The key is shown once and cannot be recovered, so it gets its own
          unmissable panel rather than a toast that scrolls away. */}
      {newKey && (
        <div className="panel" style={{ marginBottom: 16, borderLeft: '4px solid var(--warn)' }}>
          <strong>Station key for “{newKey.name}”</strong>
          <p className="muted" style={{ fontSize: 13, margin: '6px 0' }}>
            Copy this now — it is shown once and central stores only its hash. Put it in that
            station's <code>.env</code> as <code>CENTRAL_STATION_KEY</code>.
          </p>
          <code style={{ display: 'block', padding: 10, background: 'var(--panel-2)', borderRadius: 8, wordBreak: 'break-all' }}>
            {newKey.key}
          </code>
          <button className="btn sm" style={{ marginTop: 10 }} onClick={() => setNewKey(null)}>
            I've stored it
          </button>
        </div>
      )}

      {status && <SyncPanel status={status} onRan={load} />}

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
          <strong>
            Stations <span className="muted">({stations.length})</span>
          </strong>
          <span className="muted" style={{ fontSize: 13 }}>
            {totals.inspections.toLocaleString()} inspections · {totals.annotations.toLocaleString()} annotations
            collected
          </span>
        </div>

        {stations.length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>
            No stations enrolled yet. Add one below, then put its key in that machine's environment.
          </p>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Station</th>
                <th>Company / contact</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Version</th>
                <th>Last contact</th>
                <th style={{ textAlign: 'right' }}>Inspections</th>
                <th style={{ textAlign: 'right' }}>Annotations</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {stations.map((s) => {
                const health = s.active ? stationHealth(s.lastSeenAt) : 'offline';
                const h = HEALTH[health]!;
                return (
                  <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                    <td>
                      <button
                        className="btn sm"
                        style={{
                          padding: 0,
                          border: 'none',
                          background: 'none',
                          color: 'var(--accent)',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                        onClick={() => setOpenStation(s.id)}
                      >
                        {s.name}
                      </button>
                      {s.lineName && <span className="muted"> · {s.lineName}</span>}
                      {!s.active && <span className="muted"> · disabled</span>}
                      {s.lastIp && (
                        <div className="muted" style={{ fontSize: 11 }}>{s.lastIp}</div>
                      )}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {s.companyName ?? '—'}
                      {s.contactName && <div>{s.contactName}</div>}
                    </td>
                    <td>
                      {/* Who pays for this station, as a pill you can scan
                          down the column rather than a line of grey text. */}
                      <CustomerPill name={s.customer} hue={s.customerHue} />
                    </td>
                    <td>
                      <span style={{ color: h.color }}>{h.icon}</span> {h.label}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {/* Reported by the station itself. "unknown" means it has
                          not synced since this was added, or was installed
                          without git — not that something is wrong. */}
                      <code>{s.appCommit ?? 'unknown'}</code>
                      {s.updatePolicy && s.updatePolicy !== 'manual' && (
                        <div
                          style={{
                            fontSize: 11,
                            color: s.updatePolicy === 'offline' ? 'var(--text-dim)' : 'var(--warn)',
                          }}
                        >
                          {s.updatePolicy}
                        </div>
                      )}
                    </td>
                    <td className="muted">{s.lastSeenAt ? fmtTime(s.lastSeenAt) : 'never'}</td>
                    <td style={{ textAlign: 'right' }}>{s.inspections.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{s.annotations.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn sm" onClick={() => setOpenStation(s.id)} title="Open">
                        →
                      </button>
                      <button
                        className="btn sm"
                        title={
                          s.active
                            ? 'Disable — the station can no longer sync, but keeps inspecting locally'
                            : 'Re-enable this station'
                        }
                        onClick={async () => {
                          await api.updateStation(s.id, { active: !s.active });
                          await load();
                        }}
                      >
                        {s.active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="row" style={{ marginTop: 14, alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label>Enrol a station</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Line 3 — Doussard"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ minWidth: 210 }}>
            <label>Customer (who pays)</label>
            <Combobox
              options={customers.map((c) => ({ value: String(c.id), label: c.name }))}
              value={enrolCustomerId === null ? null : String(enrolCustomerId)}
              onChange={(v) => setEnrolCustomerId(v ? Number(v) : null)}
              placeholder="Search customers…"
              emptyLabel="— pick a customer —"
              allowEmpty={false}
            />
          </div>
          <button
            className="btn primary"
            disabled={!name.trim() || enrolCustomerId === null || busy}
            onClick={() => void enrol()}
          >
            {busy ? 'Enrolling…' : 'Enrol'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** This machine's own sync state: what has gone up, what is waiting. */
function SyncPanel({ status, onRan }: { status: SyncStatus; onRan: () => Promise<void> }) {
  const [running, setRunning] = useState(false);
  const pending = status.pending.inspections + status.pending.annotations;
  const failing = status.streams.find((s) => s.lastError);

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <strong>
          This machine <span className="muted">· role {status.role}</span>
        </strong>
        {status.enabled && (
          <button
            className="btn sm"
            disabled={running}
            onClick={async () => {
              setRunning(true);
              try {
                await api.runSync();
                await onRan();
              } finally {
                setRunning(false);
              }
            }}
          >
            {running ? 'Syncing…' : 'Sync now'}
          </button>
        )}
      </div>

      {!status.enabled ? (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          {status.role === 'central'
            ? 'This is the central server — stations sync up to it. It has no upstream of its own.'
            : 'Sync is off on this machine. Set CENTRAL_URL and CENTRAL_STATION_KEY to send data up.'}
        </p>
      ) : (
        <>
          <div className="row" style={{ gap: 20, flexWrap: 'wrap' }}>
            {status.streams.map((s) => (
              <div key={s.stream}>
                <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {s.stream}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{s.pushedTotal.toLocaleString()}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  sent · last run {s.lastRunAt ? fmtTime(s.lastRunAt) : 'never'}
                </div>
              </div>
            ))}
          </div>

          <p className="muted" style={{ fontSize: 13, marginBottom: 0, marginTop: 12 }}>
            {pending > 0
              ? `${pending.toLocaleString()} row${pending === 1 ? '' : 's'} waiting to go up.`
              : 'Everything local has reached central.'}
            {' '}Sending to <code>{status.url}</code>.
          </p>

          {failing && (
            // A failed sync is worth surfacing, but it is not an alarm: the line
            // is unaffected and the next run resumes from the same watermark.
            <p style={{ fontSize: 13, color: 'var(--warn)', marginBottom: 0, marginTop: 6 }}>
              ▲ Last {failing.stream} run failed: {failing.lastError}. It will retry — inspections
              are unaffected.
            </p>
          )}
        </>
      )}
    </div>
  );
}
